import React, { useState } from 'react';
import { apiService } from '../services/apiService';

// Debug page (on-site-debug-sidebar) — launches the on-site debug sidebar
// against the publisher's real site (the widget reads their live images and
// this page hands over a short-lived debug token via the URL fragment).

const launcherCardStyle: React.CSSProperties = {
  padding: '1.5rem',
  border: '1px solid rgba(1,120,254,0.24)',
  backgroundColor: 'var(--brand-soft)',
  borderRadius: 'var(--radius)',
  marginBottom: '2.5rem',
};

const errorBannerStyle: React.CSSProperties = {
  marginTop: '0.75rem',
  padding: '0.75rem 1rem',
  backgroundColor: 'var(--danger-bg)',
  color: 'var(--danger)',
  borderRadius: 'var(--radius)',
  border: '1px solid rgba(180,35,24,0.24)',
};

const DebugPage: React.FC = () => {
  const [launching, setLaunching] = useState(false);
  const [launchedDomain, setLaunchedDomain] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const launchDebugSidebar = async () => {
    setLaunching(true);
    setError(null);
    // Open synchronously within the click gesture so popup blockers allow
    // it; the launch URL is set once the token arrives. opener is severed so
    // the publisher page (and its third-party scripts) can't reach back
    // into the portal.
    const popup = window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    try {
      const { url, domain } = await apiService.mintDebugToken();
      if (popup) {
        popup.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setLaunchedDomain(domain);
    } catch (err) {
      popup?.close();
      console.error('Debug launch failed:', err);
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not start live-site debugging. Please try again shortly.'
      );
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div data-testid="debug-section" style={{ maxWidth: '800px' }}>
      <section data-testid="live-debug-launcher" style={launcherCardStyle}>
        <h2 style={{ marginTop: 0 }}>See why an image did—or did not—match</h2>
        <p style={{ color: 'var(--ink-soft)' }}>
          Opens your site with the ImgAff debug sidebar: every image on the page,
          grouped by what the widget actually did with it — skipped (and why),
          matched, no match, or errored — plus a live matcher preview per image.
          The link is private to you and expires in 15 minutes.
        </p>
        <p style={{ color: 'var(--ink-soft)' }}>
          If a result is weak, first check whether the image description accurately
          names what a reader can see. The{' '}
          <a href="https://imgaff.com/targeting-guide.html" target="_blank" rel="noreferrer">
            Image Targeting Guide
          </a>{' '}
          keeps accessibility first and explains the evidence ImgAff currently reads.
        </p>
        <button
          type="button"
          data-testid="launch-debug-sidebar"
          onClick={launchDebugSidebar}
          disabled={launching}
          className="btn-primary"
        >
          {launching ? 'Starting…' : 'Troubleshoot my live site'}
        </button>
        {launchedDomain && !error && (
          <p data-testid="launch-confirmation" style={{ color: 'var(--good)', marginBottom: 0 }}>
            Opened {launchedDomain} in a new tab with the debug sidebar active.
          </p>
        )}
        {error && (
          <div data-testid="launch-error" style={errorBannerStyle}>
            {error}
          </div>
        )}
      </section>
    </div>
  );
};

export default DebugPage;
